import {
  deleteVote,
  getEditionNomineesVotesExport,
  getEditionVoterExport,
  getVotesByEdition,
  type VoteEditionDetailResponse,
} from '@api/votes-api';
import Breadcrumbs from '@components/breadcrumbs';
import ConfirmationModal from '@components/confirmation-modal';
import { useAuth } from '@hooks/use-auth';
import { useEdition } from '@hooks/use-edition';
import { getAvatarColor, getInitials } from '@utils/avatar-utils';
import { downloadCsv } from '@utils/csv-utils';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { sileo } from 'sileo';

import {
  buildEditionNomineesVotesCsv,
  getEditionNomineesVotesCsvFilename,
} from './edition-nominees-votes-export';
import { buildEditionVotersCsv, getEditionVotersCsvFilename } from './edition-voters-export';

const PAGE_SIZE = 50;

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getParticipantFullName(vote: VoteEditionDetailResponse): string {
  return [vote.nomination.participant.name, vote.nomination.participant.surname]
    .filter(Boolean)
    .join(' ');
}

function getPageRange(currentPage: number, totalPages: number): number[] {
  const MAX_VISIBLE = 7;

  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: number[] = [1];

  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    start = 2;
    end = 5;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 4;
    end = totalPages - 1;
  }

  if (start > 2) pages.push(-1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push(-2);

  pages.push(totalPages);
  return pages;
}

export default function EditionVotes(): React.JSX.Element {
  const { editionId } = useParams<{ editionId: string }>();
  const { isAdmin } = useAuth();
  const { edition } = useEdition(editionId);
  const editionLabel = edition ? `${edition.country} ${edition.year}`.trim() : 'Edition';

  const tableRef = useRef<HTMLDivElement>(null);
  const [votes, setVotes] = useState<VoteEditionDetailResponse[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingNominees, setIsExportingNominees] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingVote, setDeletingVote] = useState<VoteEditionDetailResponse | null>(null);

  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalItems);

  const goToPage = useCallback((page: number | ((prev: number) => number)): void => {
    setCurrentPage(page);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [editionId]);

  const fetchVotes = useCallback(async (): Promise<void> => {
    if (!editionId) return;

    setIsLoading(true);
    try {
      const response = await getVotesByEdition(editionId, {
        page: currentPage,
        pageSize: PAGE_SIZE,
      });
      setVotes(response.data);
      setTotalItems(response.meta.totalItems);
      setTotalPages(Math.max(1, response.meta.totalPages));
    } catch {
      sileo.error({ title: 'Failed to load votes' });
    } finally {
      setIsLoading(false);
    }
  }, [editionId, currentPage]);

  useEffect(() => {
    void fetchVotes();
  }, [fetchVotes]);

  const handleExport = async (): Promise<void> => {
    if (!editionId || isExporting) return;

    setIsExporting(true);
    try {
      const voters = await getEditionVoterExport(editionId);
      const csv = buildEditionVotersCsv(voters);
      downloadCsv(csv, getEditionVotersCsvFilename(editionId, edition?.year));
      sileo.success({ title: 'Voters CSV exported successfully' });
    } catch {
      sileo.error({ title: 'Failed to export voters CSV' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportNominees = async (): Promise<void> => {
    if (!editionId || isExportingNominees) return;

    setIsExportingNominees(true);
    try {
      const rows = await getEditionNomineesVotesExport(editionId);
      const csv = buildEditionNomineesVotesCsv(rows);
      downloadCsv(csv, getEditionNomineesVotesCsvFilename(editionId, edition?.year));
      sileo.success({ title: 'Nominees votes CSV exported successfully' });
    } catch {
      sileo.error({ title: 'Failed to export nominees votes CSV' });
    } finally {
      setIsExportingNominees(false);
    }
  };

  const handleDelete = async (vote: VoteEditionDetailResponse): Promise<void> => {
    try {
      await deleteVote(vote.id);
      sileo.success({ title: 'Vote deleted successfully' });
      setDeletingVote(null);
      if (votes.length === 1 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      } else {
        void fetchVotes();
      }
    } catch {
      sileo.error({ title: 'Failed to delete vote' });
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Editions', to: '/editions' },
          {
            label: editionLabel,
            to: `/editions/${editionId}/categories`,
          },
          { label: 'Votes' },
        ]}
      />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">Votes</h1>
          <p className="page-header__subtitle">
            {editionLabel} &mdash; {totalItems} registered votes
          </p>
        </div>
        {isAdmin && (
          <div className="page-header__actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void handleExport()}
              disabled={isExporting}
            >
              <Download size={16} />
              {isExporting ? 'Exporting...' : 'Export voters CSV'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void handleExportNominees()}
              disabled={isExportingNominees}
            >
              <Download size={16} />
              {isExportingNominees ? 'Exporting...' : 'Export votes per nominee CSV'}
            </button>
          </div>
        )}
      </div>

      <div className="nav-links">
        <Link to={`/editions/${editionId}/categories`} className="nav-link">
          <ArrowLeft size={16} />
          Categories
        </Link>
      </div>

      <div className="stats-bar">
        <div className="stats-bar__item">
          <span className="stats-bar__label">Total votes</span>
          <span className="stats-bar__value">{totalItems}</span>
        </div>
      </div>

      {isLoading && (
        <div className="empty-state">
          <p>Loading votes...</p>
        </div>
      )}

      {!isLoading && totalItems === 0 && (
        <div className="empty-state">
          <p>No votes registered yet.</p>
        </div>
      )}

      {!isLoading && totalItems > 0 && (
        <>
          <div ref={tableRef} className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nominee</th>
                  <th>Category</th>
                  <th>Voter</th>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {votes.map((vote) => {
                  const participantName = getParticipantFullName(vote);

                  return (
                    <tr key={vote.id}>
                      <td>
                        <div className="table-cell-with-avatar">
                          <div
                            className="edition-nominee-card__avatar edition-nominee-card__avatar--sm"
                            style={{
                              backgroundColor: getAvatarColor(participantName),
                            }}
                          >
                            {getInitials(participantName)}
                          </div>
                          <strong>{participantName}</strong>
                        </div>
                      </td>
                      <td>{vote.category.name}</td>
                      <td>{vote.user.email}</td>
                      <td>{formatDate(vote.createdAt)}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            data-tooltip="Delete vote"
                            aria-label="Delete vote"
                            onClick={() => setDeletingVote(vote)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <span className="pagination__info">
                {startIndex}&ndash;{endIndex} of {totalItems}
              </span>

              <div className="pagination__controls">
                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  onClick={() => goToPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>

                {getPageRange(currentPage, totalPages).map((page) =>
                  page < 0 ? (
                    <span key={page} className="pagination__ellipsis">
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={`pagination__btn${page === currentPage ? ' pagination__btn--active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  onClick={() => goToPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmationModal
        isOpen={deletingVote !== null}
        onClose={() => setDeletingVote(null)}
        onConfirm={() => {
          if (deletingVote) return handleDelete(deletingVote);
        }}
        title="Delete vote"
        message="Are you sure you want to delete this vote? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </>
  );
}
